import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import {
  CustomizationField,
  fieldEnabled as isEnabled,
  isMandatory,
} from 'modules/expenses/hooks/useExpensesCustomization'

const defaultRequiredMessage = () => ({
  message: i18n.t('expenses_v2.forms.errors.required'),
})

export interface FormContext {
  customizationFields: CustomizationField[]
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
}

export const CostCenterSchema = ({ customizationFields, resourceType }: FormContext) => {
  return z.object({
    cost_centers: z
      .object({
        allocation: z
          .object({
            id: z.string().nullish(),
            referenceId: z.string(),
            referenceType: z.string(),
          })
          .optional(),
        assignments: z
          .object({
            assignmentsToCreate: z
              .array(
                z.object({
                  costCenterId: z.string(),
                  percentage: z.number(),
                  startDate: z.string(),
                  endDate: z.string().optional(),
                })
              )
              .optional(),
            assignmentsToUpdate: z
              .array(
                z.object({
                  id: z.string(),
                  costCenterId: z.string(),
                  percentage: z.number(),
                  startDate: z.string(),
                  endDate: z.string().optional(),
                  markedForDeletion: z.boolean().optional(),
                })
              )
              .optional(),
          })
          .optional(),
      })
      .optional()
      .refine((data) => {
        if (!shouldShowCostCenterBlock(customizationFields, resourceType)) {
          return true
        }

        if (!isMandatory('cost_centers', resourceType, customizationFields)) {
          return true
        }

        if (data?.allocation?.referenceId && data?.allocation?.referenceType) return true
        if (!data?.assignments) return false

        const { assignmentsToCreate = [], assignmentsToUpdate = [] } = data.assignments

        const totalPercentage =
          assignmentsToCreate.reduce((sum, assignment) => sum + assignment.percentage, 0) +
          assignmentsToUpdate
            .filter((assignment) => !assignment.markedForDeletion)
            .reduce((sum, assignment) => sum + assignment.percentage, 0)

        return Math.abs(totalPercentage * 100 - 100) < 0.01
      }, defaultRequiredMessage()),
  })
}

export const shouldShowCostCenterBlock = (
  customizationFields: CustomizationField[],
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
) => {
  return isEnabled(customizationFields, resourceType, 'cost_centers')
}

export type CostCenterSchema = z.infer<ReturnType<typeof CostCenterSchema>>
